import React, { useEffect, useRef, useState } from "react";
import { ukDayYear as fmtDate } from "@/lib/time";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { FileSignature, FileDown, Mail, Loader2, ShieldCheck, UploadCloud, FileCheck2, Clock, Eye, Lock, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { TermsHistory } from "@/components/portal/TermsHistory";
import { PartnerDocsPanel } from "@/components/hub/PartnerDocsPanel";

const prettySize = (n) => { n = Number(n) || 0; return n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.round(n / 1024)} KB`; };

/**
 * AgreementAdminDialog — full lifecycle per partner for BOTH documents (agreement + NDA):
 * read the exact PDF before it goes out, email it, or file a wet-ink countersigned scan.
 */
export const AgreementAdminDialog = ({ kind, party, onOpenChange, onChanged }) => {
  const [doc, setDoc] = useState("agreement");
  const [docs, setDocs] = useState(null);
  const [busy, setBusy] = useState(null);
  const [signedName, setSignedName] = useState("");
  const [scanFile, setScanFile] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    if (!party) return;
    try { const r = await api.get(`/hub/agreements/${kind}/${party.id}/documents`); setDocs(r.data); }
    catch { setDocs(null); }
  };
  useEffect(() => { setDoc("agreement"); setScanFile(null); setSignedName(""); load(); }, [party?.id]); // eslint-disable-line
  if (!party) return null;

  const current = docs?.[doc] || (doc === "agreement" ? party.agreement || {} : {});
  const signed = current.status === "signed";
  const name = party.company_name || party.name;
  const isNda = doc === "nda";
  const label = isNda ? "Mutual NDA" : kind === "lender" ? "Lender panel agreement" : "Introducer agreement";

  const fetchBlob = async () => {
    // `v` defeats browser/CDN caching so a rate change is always reflected immediately
    const r = await api.get(`/hub/agreements/${kind}/${party.id}.pdf`, { params: { doc, v: Date.now() }, responseType: "blob" });
    return URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
  };
  const preview = async () => {
    setBusy("preview");
    try { window.open(await fetchBlob(), "_blank", "noopener"); }
    catch { toast.error("Could not open the preview"); }
    finally { setBusy(null); }
  };
  const dlPdf = async () => {
    setBusy("pdf");
    try {
      const url = await fetchBlob();
      const a = document.createElement("a");
      a.href = url; a.download = `connexis-${isNda ? "nda" : "agreement"}-${(name || "partner").replace(/\s+/g, "-").toLowerCase()}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch { toast.error("Download failed"); }
    finally { setBusy(null); }
  };
  const dlScan = async () => {
    setBusy("scan");
    try {
      const r = await api.get(`/hub/agreements/${kind}/${party.id}/scan`, { params: { doc }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a"); a.href = url; a.download = current.scan_filename || `${doc}-scan`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch { toast.error("Scan download failed"); }
    finally { setBusy(null); }
  };
  const send = async (which) => {
    setBusy(`send-${which}`);
    try {
      await api.post(`/hub/agreements/${kind}/${party.id}/send`, null, { params: { doc: which } });
      toast.success(which === "both" ? `Agreement + NDA emailed to ${party.email}` : `${which === "nda" ? "NDA" : "Agreement"} emailed to ${party.email}`);
      await load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setBusy(null); }
  };
  const countersign = async () => {
    if (!scanFile) return;
    setBusy("sign");
    try {
      const fd = new FormData();
      fd.append("file", scanFile);
      fd.append("signed_name", signedName);
      fd.append("doc", doc);
      await api.post(`/hub/agreements/${kind}/${party.id}/countersign`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${label} marked as executed — confirmation emailed to the partner`);
      setScanFile(null);
      await load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Countersign failed"); }
    finally { setBusy(null); }
  };

  const reissue = async () => {
    setBusy("reissue");
    try {
      const r = await api.post(`/hub/agreements/${kind}/${party.id}/reissue`, null, { params: { doc } });
      toast.success(`Re-issued at the new terms as v${r.data.version}${r.data.delivered ? " and emailed for countersignature" : " — email failed, but it's live in their portal"}`);
      await load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Re-issue failed"); }
    finally { setBusy(null); }
  };

  const TabBtn = ({ id, Icon, children }) => {
    const st = docs?.[id]?.status;
    return (
      <button onClick={() => { setDoc(id); setScanFile(null); }}
              className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg h-9 text-[12.5px] font-semibold border transition-colors ${
                doc === id ? "border-[#0055FF] bg-[#0055FF]/[0.08] text-[#0055FF]" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}
              data-testid={`hub-doc-tab-${id}`}>
        <Icon className="size-3.5" /> {children}
        <span className={`size-1.5 rounded-full ${st === "signed" ? "bg-teal-500" : st === "sent" ? "bg-[#7da9ff]" : "bg-slate-300"}`} />
      </button>
    );
  };

  return (
    <Dialog open={!!party} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto" data-testid="hub-agreement-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileSignature className="size-4 text-[#0055FF]" /> Documents</DialogTitle>
          <DialogDescription>{name} · {party.email}</DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2">
          <TabBtn id="agreement" Icon={FileSignature}>Agreement</TabBtn>
          <TabBtn id="nda" Icon={Lock}>NDA</TabBtn>
        </div>

        {signed ? (
          <div className="rounded-xl border border-teal-200 bg-teal-50 p-3.5 flex items-start gap-2.5" data-testid="hub-agreement-status">
            <ShieldCheck className="size-4 text-teal-600 shrink-0 mt-0.5" />
            <div className="text-[12.5px] text-teal-800">
              <b>{label} executed {fmtDate(current.signed_at)}</b> by {current.signed_name} · {current.method === "wet_ink" ? "wet-ink signature (scan on file)" : "countersigned in the portal"}
            </div>
          </div>
        ) : (
          <div className="rounded-xl border border-[#7da9ff]/45 bg-[#7da9ff]/[0.10] p-3.5 flex items-start gap-2.5" data-testid="hub-agreement-status">
            <Clock className="size-4 text-[#3B63A8] shrink-0 mt-0.5" />
            <div className="text-[12.5px] text-[#2F5390]">
              {current.status === "sent"
                ? <><b>Sent {fmtDate(current.sent_at)}</b> — awaiting their countersignature (auto-reminders every 3 days by email + SMS)</>
                : <><b>Not sent yet</b> — read it below, then email it or file a paper copy</>}
            </div>
          </div>
        )}

        {current.terms_changed && (
          <div className="rounded-xl border border-[#7da9ff]/60 bg-[#7da9ff]/[0.10] p-3.5 space-y-2.5" data-testid="hub-terms-changed">
            <div className="text-[12.5px] text-[#24457F]">
              <b>Terms have moved on since this was signed.</b><br />
              Executed at <b>{current.signed_terms?.rate}</b> ({current.signed_terms?.short}) · now on <b>{current.live_terms?.rate}</b> ({current.live_terms?.short}).
              The executed PDF keeps the signed terms — re-issue to put the new terms in front of them.
            </div>
            <Button onClick={reissue} disabled={busy === "reissue"} className="w-full rounded-xl bg-[#0055FF] hover:bg-[#0044CC] h-9 text-[12.5px] font-semibold" data-testid="hub-reissue-agreement">
              {busy === "reissue" ? <Loader2 className="size-4 animate-spin" /> : <><RefreshCw className="size-3.5 mr-1.5" /> Re-issue at {current.live_terms?.rate} for countersignature</>}
            </Button>
          </div>
        )}

        {!signed && current.delivery_error && (
          <div className="rounded-xl border border-[#0055FF]/30 bg-[#0055FF]/[0.07] p-3 text-[12px] text-[#0044CC]" data-testid="hub-doc-delivery-error">
            <b>Email didn't reach them.</b> {current.delivery_error} — the document is still live in their portal, but it's worth fixing the address and resending.
          </div>
        )}

        <div className="grid grid-cols-2 gap-2">
          <Button variant="outline" onClick={preview} disabled={busy === "preview"} className="rounded-xl" data-testid={`hub-${kind}-doc-preview`}>
            {busy === "preview" ? <Loader2 className="size-4 animate-spin" /> : <><Eye className="size-3.5 mr-1.5" /> Read it first</>}
          </Button>
          <Button variant="outline" onClick={dlPdf} disabled={busy === "pdf"} className="rounded-xl" data-testid={`hub-${kind}-agreement-pdf`}>
            {busy === "pdf" ? <Loader2 className="size-4 animate-spin" /> : <><FileDown className="size-3.5 mr-1.5" /> Download PDF</>}
          </Button>
        </div>

        {signed ? (
          current.has_scan && (
            <Button variant="outline" onClick={dlScan} disabled={busy === "scan"} className="rounded-xl w-full" data-testid="hub-agreement-scan">
              {busy === "scan" ? <Loader2 className="size-4 animate-spin" /> : <><FileCheck2 className="size-3.5 mr-1.5" /> Download the signed scan</>}
            </Button>
          )
        ) : (
          <div className="grid grid-cols-2 gap-2">
            <Button onClick={() => send(doc)} disabled={busy === `send-${doc}`} className="rounded-xl bg-[#0055FF] hover:bg-[#0044CC]" data-testid={`hub-${kind}-agreement-send-${party.id}`}>
              {busy === `send-${doc}` ? <Loader2 className="size-4 animate-spin" /> : <><Mail className="size-3.5 mr-1.5" /> Email this one</>}
            </Button>
            <Button onClick={() => send("both")} disabled={busy === "send-both"} variant="outline" className="rounded-xl" data-testid="hub-agreement-send-both">
              {busy === "send-both" ? <Loader2 className="size-4 animate-spin" /> : <><Mail className="size-3.5 mr-1.5" /> Send both</>}
            </Button>
          </div>
        )}

        {!signed && (
          <div className="min-w-0 rounded-2xl border border-slate-200 bg-slate-50/60 p-4 space-y-3" data-testid="hub-countersign-panel">
            <div className="text-[10.5px] uppercase tracking-[0.18em] font-semibold text-slate-600">Received a paper copy? File it here</div>
            <div
              onClick={() => fileRef.current?.click()}
              className="rounded-xl border-2 border-dashed border-slate-300 bg-white p-4 text-center cursor-pointer hover:border-[#0055FF]/40 transition-colors"
              data-testid="hub-countersign-drop"
            >
              <UploadCloud className="size-5 text-slate-400 mx-auto" />
              <div className="mt-1.5 text-[12.5px] font-semibold text-slate-800 truncate" title={scanFile ? scanFile.name : undefined}>
                {scanFile ? scanFile.name : `Upload the signed ${isNda ? "NDA" : "agreement"} (PDF, JPG or PNG)`}
              </div>
              <div className="mt-0.5 text-[11px] text-slate-400">
                {scanFile ? prettySize(scanFile.size) : "Up to 75MB — a phone photo or a full scan is fine"}
              </div>
              <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden"
                     onChange={(e) => { setScanFile(e.target.files?.[0] || null); e.target.value = ""; }}
                     data-testid="hub-countersign-file" />
            </div>
            <Input value={signedName} onChange={(e) => setSignedName(e.target.value)}
                   placeholder={`Signatory name (default: ${party.contact_name || party.name || "contact"})`}
                   data-testid="hub-countersign-name" />
            <Button onClick={countersign} disabled={!scanFile || busy === "sign"} className="w-full rounded-xl bg-slate-900 hover:bg-slate-800" data-testid="hub-countersign-submit">
              {busy === "sign" ? <Loader2 className="size-4 animate-spin" /> : <><FileSignature className="size-3.5 mr-1.5" /> Mark as executed (wet-ink)</>}
            </Button>
            <div className="text-[10.5px] text-slate-400 text-center">The partner gets an executed copy by email; the scan is stored securely on file.</div>
          </div>
        )}

        <TermsHistory doc={doc} reloadKey={`${party.id}-${docs?.[doc]?.version || 1}-${docs?.[doc]?.status || ""}`}
                      historyPath={`/hub/agreements/${kind}/${party.id}/history`}
                      versionPath={(v) => `/hub/agreements/${kind}/${party.id}/history/${v}.pdf`}
                      notifyPath={doc === "agreement" ? `/hub/agreements/${kind}/${party.id}/terms-notice` : undefined} />

        <PartnerDocsPanel kind={kind} partyId={party.id} partyName={name} />
      </DialogContent>
    </Dialog>
  );
};
